import { BlockBlobClient } from '@azure/storage-blob'
import { client } from 'src/enviroment/api/axios'
import { InfiniteData, QueryKey, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { Mutation, Query } from '../index'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { getMimeType } from 'src/enviroment/lib/fileUtils'
import { queryTool } from 'src/enviroment/lib/utils'
import {
  LayerFilters,
  LayerItem,
  LayersResponse,
  LayerUploadStatus,
  UpdateLayerMetadataPayload,
} from '../../types/layer'

export interface ToggleVisibilityData {
  fileId: string
  visible: boolean
}

export interface ToggleVisibilityResponse {
  newState: boolean
}

export interface ToggleVisibilityContext {
  previousLists: [QueryKey, InfiniteData<LayersResponse> | undefined][]
}

const LAYERS_PAGE_SIZE = 100

export const layerRouterKeys = {
  layers: 'layer.layers',
  countries: 'layer.countries',
  uploadStatus: 'layer.uploadStatus',
} as const

export const layerRouter = {
  list: {
    useInfiniteQuery: ({ query, ...filters }: Partial<LayerFilters> & { query?: string }) => {
      return useInfiniteQuery({
        queryKey: [layerRouterKeys.layers, query, filters],
        queryFn: async ({ pageParam = 0 }) => {
          const params = { ...filters, query: queryTool(query?.trim()), skip: pageParam, take: LAYERS_PAGE_SIZE }
          return (await client.get<LayersResponse>('/layers', { params })).data
        },
        getNextPageParam: (lastPage) => lastPage.nextSkip ?? undefined,
        keepPreviousData: true,
      })
    },
  },
  countries: {
    useQuery: (opts?: Query<string[]>) => {
      return useQuery<string[]>({
        queryKey: [layerRouterKeys.countries],
        queryFn: async () => {
          return (await client.get<string[]>('/layers/countries')).data
        },
        ...opts,
      })
    },
  },
  uploadStatus: {
    useQuery: (fileName: string | null) => {
      const queryClient = useQueryClient()

      return useQuery({
        queryKey: [layerRouterKeys.uploadStatus, fileName],
        queryFn: async () => {
          const params = { fileName }
          return (await client.get<{ status: LayerUploadStatus | null }>('/layers/upload-status', { params })).data
        },
        enabled: Boolean(fileName),
        refetchInterval: 10000,
        // A deleted and re-uploaded file must not read the previous upload's status.
        cacheTime: 0,
        // The row appears only after the geo-metadata callback, well after the blob upload ends.
        onSuccess: ({ status }) => {
          if (status) queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
        },
      })
    },
  },
  delete: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async (fileId: string) => {
          await client.delete(`/layers/${fileId}`)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('File deleted successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  updateName: {
    useMutation: (opts?: Mutation<{ fileId: string; fileName: string }, LayerItem>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, fileName }: { fileId: string; fileName: string }) => {
          return (await client.patch<LayerItem>(`/layers/${fileId}`, { fileName })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('File name updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  updateOrder: {
    useMutation: (opts?: Mutation<{ fileId: string; orderNumber: number | null }, LayerItem>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, orderNumber }: { fileId: string; orderNumber: number | null }) => {
          return (await client.patch<LayerItem>(`/layers/${fileId}`, { orderNumber })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Order updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  updateDescription: {
    useMutation: (opts?: Mutation<{ fileId: string; description: string | null }, LayerItem>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, description }: { fileId: string; description: string | null }) => {
          return (await client.patch<LayerItem>(`/layers/${fileId}`, { description })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Description updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  updateMetadata: {
    useMutation: (opts?: Mutation<{ fileId: string; data: UpdateLayerMetadataPayload }, LayerItem>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, data }: { fileId: string; data: UpdateLayerMetadataPayload }) => {
          return (await client.patch<LayerItem>(`/layers/${fileId}`, data)).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Layer updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  approve: {
    useMutation: (opts?: Mutation<string, LayerItem>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async (fileId: string) => {
          return (await client.patch<LayerItem>(`/layers/${fileId}/approve`)).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Layer approved successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  uploadStyle: {
    useMutation: (opts?: Mutation<{ fileId: string; style: object }, unknown>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, style }: { fileId: string; style: object }) => {
          return (await client.put(`/layers/${fileId}/style`, { style })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Style uploaded successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  uploadComponents: {
    useMutation: (opts?: Mutation<{ fileId: string; widgets: object }, unknown>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async ({ fileId, widgets }: { fileId: string; widgets: object }) => {
          return (await client.put(`/layers/${fileId}/widgets`, { widgets })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          enqueueSnackbar('Components uploaded successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
      })
    },
  },
  toggleVisibility: {
    useMutation: (opts?: Mutation<ToggleVisibilityData, ToggleVisibilityResponse>) => {
      const queryClient = useQueryClient()

      return useMutation<ToggleVisibilityResponse, Error, ToggleVisibilityData, ToggleVisibilityContext>({
        ...opts,
        mutationFn: async ({ fileId, visible }: ToggleVisibilityData) => {
          return (await client.put<ToggleVisibilityResponse>(`/layers/${fileId}/visibility`, { visible })).data
        },
        onMutate: async ({ fileId, visible }) => {
          await queryClient.cancelQueries({ queryKey: [layerRouterKeys.layers] })
          const previousLists = queryClient.getQueriesData<InfiniteData<LayersResponse>>({
            queryKey: [layerRouterKeys.layers],
          })

          queryClient.setQueriesData<InfiniteData<LayersResponse>>({ queryKey: [layerRouterKeys.layers] }, (old) => {
            if (!old) return old
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                layers: page.layers.map((layer) =>
                  layer.id === fileId ? { ...layer, globallyVisibleStatus: visible } : layer,
                ),
              })),
            }
          })

          return { previousLists }
        },
        onError: (error, variables, context) => {
          context?.previousLists.forEach(([key, data]) => queryClient.setQueryData(key, data))
          handleApiError(error)
          opts?.onError?.(error, variables, context)
        },
        onSettled: (...args) => {
          queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
          opts?.onSettled?.(...args)
        },
        onSuccess: (...args) => {
          enqueueSnackbar('Visibility updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
      })
    },
  },
  directUpload: {
    useUpload: () => {
      const queryClient = useQueryClient()

      const generateSasUrlMutation = useMutation({
        mutationFn: async (fileName: string) => {
          return (
            await client.post<{ sasUrl: string }>('/layers/generate-sas-url', {
              fileName,
              expiresInMinutes: 60,
            })
          ).data
        },
        onError: handleApiError,
      })

      const upload = async (
        file: File,
        signal?: AbortSignal,
        onProgress?: (progress: { loadedBytes: number; totalBytes: number; percentage: number }) => void,
      ) => {
        const { sasUrl } = await generateSasUrlMutation.mutateAsync(file.name)

        // Files > 256MB are uploaded in chunks, less are uploaded in one go.
        const blockBlobClient = new BlockBlobClient(sasUrl)

        await blockBlobClient.uploadData(file, {
          blockSize: 10 * 1024 * 1024,
          concurrency: 5,
          blobHTTPHeaders: { blobContentType: getMimeType(file) },
          abortSignal: signal,
          onProgress: ({ loadedBytes }) =>
            onProgress?.({
              loadedBytes,
              totalBytes: file.size,
              percentage: Math.round((loadedBytes / file.size) * 100),
            }),
        })

        await queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
      }

      return {
        upload,
        isLoading: generateSasUrlMutation.isPending,
      }
    },
  },

  // NOTE: DEPRECATED — chunked upload flow is unused, replaced by directUpload
  // initUpload: {
  //   useMutation: (opts?: Mutation<{ filename: string; mimeType: string; sizeBytes: number; totalChunks: number }, { fileId: string }>) => {
  //     return useMutation({
  //       ...opts,
  //       mutationFn: async (data: { filename: string; mimeType: string; sizeBytes: number; totalChunks: number }) => {
  //         return (await client.post<{ fileId: string }>('/layers/init', data)).data
  //       },
  //       onError: (...args) => {
  //         handleApiError(args[0])
  //         opts?.onError?.(...args)
  //       },
  //     })
  //   },
  // },
  // uploadChunk: {
  //   useMutation: (opts?: Mutation<{ fileId: string; chunk: Blob; chunkIndex: number; signal?: AbortSignal }, void>) => {
  //     return useMutation({
  //       ...opts,
  //       mutationFn: async ({ fileId, chunk, chunkIndex, signal }: { fileId: string; chunk: Blob; chunkIndex: number; signal?: AbortSignal }) => {
  //         const formData = new FormData()
  //         formData.append('chunk', chunk)
  //         formData.append('chunkIndex', chunkIndex.toString())
  //         await client.post(`/layers/chunk/${fileId}`, formData, { signal })
  //       },
  //       onError: (...args) => {
  //         handleApiError(args[0])
  //         opts?.onError?.(...args)
  //       },
  //     })
  //   },
  // },
  // chunkedUpload: {
  //   useUpload: (chunkSize: number = 5 * 1024 * 1024) => {
  //     const queryClient = useQueryClient()
  //     const initUploadMutation = useMutation({
  //       mutationFn: async (data: { filename: string; mimeType: string; sizeBytes: number; totalChunks: number }) => {
  //         return (await client.post<{ fileId: string }>('/layers/init', data)).data
  //       },
  //       onError: handleApiError,
  //     })
  //     const uploadChunkMutation = useMutation({
  //       mutationFn: async ({ fileId, chunk, chunkIndex, signal }: { fileId: string; chunk: Blob; chunkIndex: number; signal?: AbortSignal }) => {
  //         const formData = new FormData()
  //         formData.append('chunk', chunk)
  //         formData.append('chunkIndex', chunkIndex.toString())
  //         await client.post(`/layers/chunk/${fileId}`, formData, { signal })
  //       },
  //       onError: handleApiError,
  //     })
  //
  //     const upload = async (
  //       file: File,
  //       { onProgress, signal }: {
  //         onProgress?: (progress: { chunks: number; totalChunks: number; percentage: number }) => void
  //         signal?: AbortSignal
  //       } = {},
  //     ) => {
  //       try {
  //         const totalChunks = Math.ceil(file.size / chunkSize)
  //         const { fileId } = await initUploadMutation.mutateAsync({
  //           filename: file.name,
  //           mimeType: getMimeType(file),
  //           sizeBytes: file.size,
  //           totalChunks,
  //         })
  //
  //         for (let i = 0; i < totalChunks; i++) {
  //           const start = i * chunkSize
  //           const end = Math.min(start + chunkSize, file.size)
  //           const chunk = file.slice(start, end)
  //           await uploadChunkMutation.mutateAsync({ fileId, chunk, chunkIndex: i, signal })
  //           onProgress?.({ chunks: i + 1, totalChunks, percentage: Math.round(((i + 1) / totalChunks) * 100) })
  //         }
  //
  //         await queryClient.invalidateQueries({ queryKey: [layerRouterKeys.layers] })
  //         enqueueSnackbar('File uploaded successfully', { variant: 'success' })
  //         return fileId
  //       } catch (error) {
  //         handleApiError(error)
  //         throw error
  //       }
  //     }
  //
  //     return {
  //       upload,
  //       isLoading: initUploadMutation.isPending || uploadChunkMutation.isPending,
  //     }
  //   },
  // },

  // NOTE: DEPRECATED — non-hook direct function, never used
  // updateLayerNameDirect: async (id: string, newName: string): Promise<LayerItem> => {
  //   const response = await client.patch(`/layers/${id}`, { fileName: newName })
  //   return response.data
  // },
}
